import { Directive, ElementRef, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

export interface IAutoCompleteScrollEvent {
  autoComplete: MatAutocomplete;
  action: string;
}

@Directive({
    selector: 'mat-autocomplete[optionsInfiniteScroll]',
    standalone: false
})
export class InfiniteScrollDirective implements OnDestroy {
  private readonly _onDestroy = new Subject();
  private _pageLoadedSubscription: Subscription;
  private _isPanelOpen: boolean = false;
  private _canLoad: boolean = true;
  private _pageLoaded: Observable<number>;

  readonly onScroll = () => this.nextPage();

  @Output('optionsInfiniteScroll') scroll = new EventEmitter<IAutoCompleteScrollEvent>();
  @Input() set pageLoaded(value: Observable<number>) {
    // TODO: rewrite with Input of Items array + onChanges of Items instead?
    this._pageLoaded = value;
    this._setupOnPageLoaded();
  }
  get pageLoaded(): Observable<number> {
    return this._pageLoaded;
  }

  private get _panelElement(): Element {
    return this.autoComplete?.panel?.nativeElement;
  }

  private get _needLoad(): boolean {
    return this._hasEmptySpace || this._hasScrolledBottom;
  }
  // Checks the height of the container to see if it's necessary to load more items immediately
  private get _hasEmptySpace(): boolean {
    const el = this._panelElement;
    return el.scrollHeight < el.clientHeight + 5;
  }

  // Checks if the scroll position of the container has passed the 90% of range
  private get _hasScrolledBottom(): boolean {
    const el = this._panelElement;
    const percentage = (el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100;
    return percentage >= 90;
  }

  constructor(
    el: ElementRef,
    public readonly autoComplete: MatAutocomplete
  ) {
    this._setupOnPanelOpened();
    this._setupOnPanelClosed();
  }

  ngOnDestroy() {
    this._onDestroy.next(null);
    this._onDestroy.complete();
    this._pageLoadedSubscription?.unsubscribe();
    this._removeScrollEventListener();
  }

  // Triggers the load-more items function
  nextPage() {
    if (this._canLoad && this._needLoad) {
      this._canLoad = false;
      this.scroll.next({ autoComplete: this.autoComplete, action: 'nextpage' });
    }
  }

  private _addScrollEventListener() {
    this._panelElement?.addEventListener('scroll', this.onScroll);
  }

  private _removeScrollEventListener() {
    this._panelElement?.removeEventListener('scroll', this.onScroll);
    this.scroll.next({ autoComplete: this.autoComplete, action: 'reset' });
  }

  private _setupOnPageLoaded() {
    this._pageLoadedSubscription?.unsubscribe();
    this._pageLoadedSubscription = this._pageLoaded.subscribe(() => {
      console.debug('is:pageloaded');
      this._canLoad = true;
      if (this._isPanelOpen) {
        this.nextPage();
      }
    });
  }

  private _setupOnPanelOpened(): void {
    this.autoComplete.opened
      .pipe(
        tap(() => {
          /** Note: When autocomplete raises opened, panel is not yet created (by Overlay)
            Note: The panel will be available on next tick
            Note: The panel wil NOT open if there are no options to display
        */
          setTimeout(() => {
            this._isPanelOpen = true;
            // console.log('is:opened');

            /** Note: remove listner just for safety, in case the close event is skipped. */
            this._removeScrollEventListener();
            this._addScrollEventListener();

            this.nextPage();
          });
        }),
        takeUntil(this._onDestroy)
      )
      .subscribe();
  }

  private _setupOnPanelClosed(): void {
    this.autoComplete.closed
      .pipe(
        tap(() => {
          this._isPanelOpen = false;
          this._removeScrollEventListener();
        }),
        takeUntil(this._onDestroy)
      )
      .subscribe();
  }
}
